import { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsUpDown, faXmark, faPlus } from "@fortawesome/free-solid-svg-icons";

import CustomForm from "../../general/form/CustomForm";
import SelectorSortableOption from "./SelectorSortableOption";
import { handleSubmit } from "../../../auxiliar/auxFunctions";

const MainSortableField = ({ id, position, onRemove, register, errors, watch, setValue }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const name = watch(`${id}.label`);
    const type = watch(`${id}.type`);
    const [newOption, setNewOption] = useState("");
    const sensors = useSensors(useSensor(PointerSensor));

    const handleAddOption = () => {
        if (!newOption.trim()) return;

        const currentOptions = watch(`${id}.options`) || [];
        setValue(`${id}.options`, [...currentOptions, { key: uuidv4(), label: newOption.trim() }]);
        setNewOption("");
    };

    const handleRemoveOption = (indexToRemove) => {
        const currentOptions = watch(`${id}.options`) || [];
        if (currentOptions.length <= 1) return;
        setValue(
            `${id}.options`,
            currentOptions.filter((_, index) => index !== indexToRemove),
        );
    };

    useEffect(() => {
        const currentOptions = watch(`${id}.options`) || [];
        if (type === "selector" && currentOptions.length === 0) {
            setValue(`${id}.options`, [{ key: "select", label: "Selecciona una opción" }]);
            setValue(`${id}.defaultValue`, "select");
        }
    }, [id, setValue, watch, type]);

    const handleOptionDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            const currentOptions = watch(`${id}.options`) || [];
            const oldIndex = currentOptions.findIndex(
                (option) => `option-${option.key}` === active.id,
            );
            const newIndex = currentOptions.findIndex(
                (option) => `option-${option.key}` === over.id,
            );

            setValue(`${id}.options`, arrayMove(currentOptions, oldIndex, newIndex));
        }
    };

    const getDefaultFieldConfig = () => {
        switch (type) {
            case "number":
                return {
                    condition: true,
                    type: "number",
                    label: "Valor por defecto",
                    name: `${id}.defaultValue`,
                };
            case "date":
                return {
                    condition: true,
                    type: "date",
                    label: "Valor por defecto",
                    name: `${id}.defaultValue`,
                };
            case "datetime-local":
                return {
                    condition: true,
                    type: "datetime-local",
                    label: "Valor por defecto",
                    name: `${id}.defaultValue`,
                };
            case "time":
                return {
                    condition: true,
                    type: "time",
                    label: "Valor por defecto",
                    name: `${id}.defaultValue`,
                };
            case "checkbox":
                return {
                    condition: true,
                    type: "checkbox",
                    label: "Marcado por defecto",
                    name: `${id}.defaultValue`,
                };
            case "textarea":
                return {
                    condition: true,
                    type: "textarea",
                    label: "Valor por defecto",
                    name: `${id}.defaultValue`,
                };
            case "selector":
                return {
                    condition: true,
                    type: "selector",
                    label: "Valor por defecto",
                    name: `${id}.defaultValue`,
                    options:
                        (watch(`${id}.options`) || []).length === 0
                            ? [{ key: "select", label: "Selecciona una opción" }]
                            : (watch(`${id}.options`) || []).map((option) => ({
                                  key: option.key,
                                  label: option.label,
                              })),
                    validators: {
                        required: "El valor por defecto es obligatorio",
                    },
                };
            default:
                return {
                    condition: true,
                    type: "text",
                    label: "Valor por defecto",
                    name: `${id}.defaultValue`,
                };
        }
    };

    const formConfig = {
        label: {
            condition: true,
            type: "text",
            label: "Nombre del campo*",
            name: `${id}.label`,
            validators: {
                required: "El nombre es obligatorio",
            },
        },
        type: {
            condition: true,
            type: "selector",
            label: "Tipo del campo*",
            name: `${id}.type`,
            options: [
                { key: "text", label: "Texto" },
                { key: "textarea", label: "Área de texto grande" },
                { key: "number", label: "Número" },
                { key: "checkbox", label: "Checkbox" },
                { key: "selector", label: "Selector" },
                { key: "date", label: "Fecha" },
                { key: "datetime-local", label: "Fecha y hora" },
                { key: "time", label: "Hora" },
            ],
            validators: {
                required: "El tipo es obligatorio",
            },
        },
        defaultValue: getDefaultFieldConfig(),
        dummy: {
            condition: !["checkbox", "textarea"].includes(type),
        },
        required: {
            condition: true,
            type: "checkbox",
            label: "Campo obligatorio",
            name: `${id}.required`,
            info:
                type === "selector"
                    ? "Si está marcada, una opción que no sea 'Selecciona una opción' será obligatoria"
                    : "Si está marcado, este campo será obligatorio en el formulario",
        },
    };

    return (
        <div
            ref={setNodeRef}
            className="border border-base-300 rounded-md p-6 bg-base-200 relative"
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
            }}
        >
            <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                    <span className="font-medium">
                        {position}: {name}
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            className="cursor-grab active:cursor-grabbing px-3 hover:bg-base-300 hover:cursor-pointer rounded"
                            {...attributes}
                            {...listeners}
                        >
                            <FontAwesomeIcon icon={faArrowsUpDown} className="text-primary" />
                        </button>
                        <button
                            onClick={onRemove}
                            className="px-3 hover:bg-base-300 rounded text-error hover:cursor-pointer"
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    </div>
                </div>

                <form>
                    <CustomForm
                        formConfig={formConfig}
                        errors={id in errors ? errors[id] : {}}
                        register={register}
                        className="grid grid-cols-1 md:grid-cols-2 gap-3"
                    />

                    {type === "selector" && (
                        <div className="mt-3">
                            <div className="flex flex-col gap-2">
                                <label className="label-text">Opciones del selector</label>

                                {(watch(`${id}.options`) || []).length > 0 ? (
                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={handleOptionDragEnd}
                                        modifiers={[restrictToVerticalAxis]}
                                    >
                                        <SortableContext
                                            items={(watch(`${id}.options`) || []).map(
                                                (option) => `option-${option.key}`,
                                            )}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            <div className="flex flex-col gap-2">
                                                {(watch(`${id}.options`) || []).map(
                                                    (option, index) => (
                                                        <SelectorSortableOption
                                                            key={`option-${option.key}`}
                                                            id={`option-${option.key}`}
                                                            value={option.label}
                                                            onRemove={handleSubmit(
                                                                handleRemoveOption,
                                                                index,
                                                            )}
                                                        />
                                                    ),
                                                )}
                                            </div>
                                        </SortableContext>
                                    </DndContext>
                                ) : (
                                    <div className="text-center text-base-content/70 py-8">
                                        No hay opciones añadidas. Añade opciones usando el campo de
                                        abajo.
                                    </div>
                                )}

                                <div className="flex gap-2 items-center">
                                    <input
                                        type="text"
                                        value={newOption}
                                        onChange={(e) => setNewOption(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                handleAddOption();
                                            }
                                        }}
                                        placeholder="Nueva opción"
                                        className="input input-bordered flex-1 input-primary"
                                    />
                                    <button
                                        onClick={handleSubmit(handleAddOption)}
                                        className="btn btn-primary btn-sm"
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default MainSortableField;
